import { Form, FormInstance } from 'antd';
import { Store } from 'antd/lib/form/interface';
import moment from 'moment';
import React, { useMemo } from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemFilesListV2 } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemV2Type } from '@onboarding/graphql';
import { UpdateNurseHealthDoc } from 'works/graphql/nurse_health_doc';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemV2Type;
  refetchQueries: string[];
  onDeleted?: () => void;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemUpdateFormHealthDoc({
  form,
  complianceItem,
  refetchQueries,
  onDeleted,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update license. Please try again.');

  const [updateNurseHealthDoc, { loading: formSubmitting }] = useMutation(
    UpdateNurseHealthDoc,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseHealthDoc.errors) {
          addErrorsToForm(form, response.updateNurseHealthDoc.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Document has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const initialValues = useMemo(
    () => ({
      completedOn: detailItem.completedOn && moment(detailItem.completedOn),
      expiresOn: detailItem.expiresOn && moment(detailItem.expiresOn),
    }),
    [detailItem]
  );

  if (!detailItem) {
    return null;
  }

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    updateNurseHealthDoc({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}
    >
      <ComplianceItemFilesListV2
        refetchQueries={refetchQueries}
        complianceItemId={complianceItem.id}
        complianceItemStateApprovedAt={complianceItem.stateApprovedAt}
        attachmentList={detailItem.detailItemAttachments}
        onDeleted={onDeleted}
        formContent={
          <ComplianceItemInputExpirationDate
            expirationType={complianceItem.expirationType}
          />
        }
      />
    </Form>
  );
}
